import { isPrerendering } from 'services/prerender'
import {
  fetchQuery as fetchRelayQuery,
  commitMutation as commitRelayMutation,
  requestSubscription as requestRelaySubscription,
  commitLocalUpdate,
  GraphQLTaggedNode,
} from 'react-relay'
import {
  GraphQLResponseWithData,
  GraphQLSubscriptionConfig,
  MutationConfig,
  MutationParameters,
  OperationType,
  PayloadError,
} from 'relay-runtime'
import { trackError } from 'services/analytics'
import RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment'
import { omit } from 'lodash'
import { GraphQLError, Nullable } from 'types'
import { getErrorSeverity, isGraphQLError, objectToError } from 'services/error'
import { getClientEnvironment } from './relay/clientEnvironment'
import { fetchGraphQLRaw } from './raw'
import { graphQLErrorExtraData, hasGraphQLErrors } from './error'
import { clearSavedRecords } from './relay/recordSource'

export function generateErrorFromGraphQLError(error: GraphQLError | PayloadError) {
  if (isGraphQLError(error) && error?.extensions) {
    console.error('GraphQL Error Extensions:', error.extensions)
  }

  return objectToError(error)
}

export function generateErrorFromGraphQLErrors(errors: Nullable<(GraphQLError[] | PayloadError[])>) {
  if (!errors?.[0]) return null

  return generateErrorFromGraphQLError(errors[0])
}

/**
 * Method to execute a GraphQL query outside of the Relay context. Meant to be used in the browser console, where it is
 * exposed as fetchGraphQL.
 * @param query the query to fetch
 * @param variables variables (if any)
 * @param cookie cookie to use for authentication. This won't be automatically set.
 */
export async function fetchGraphQL(query: string, variables: any = {}, cookie?: string) {
  const response = await fetchGraphQLRaw(query, variables, cookie)

  if (response.headers.get('content-type') !== 'application/json') {
    if (response.status === 504) {
      trackError(
        new Error('API timed out'),
        'high',
        {
          code: response.status,
        },
      )

      throw new Error('Operation took too long. Please try again later')
    } else {
      trackError(
        new Error('API content type is not json'),
        'high',
        {
          code: response.status,
        },
      )

      throw new Error('Something went wrong')
    }
  }

  const responseData: GraphQLResponseWithData = await response.json()

  if (hasGraphQLErrors(responseData)) {
    trackError(
      generateErrorFromGraphQLErrors(responseData.errors),
      getErrorSeverity(responseData.errors?.[0]) || 'high',
      graphQLErrorExtraData({ query, variables, cookie }),
    )
  }

  return responseData?.data
}

export type RelayCacheConfig = Parameters<typeof fetchRelayQuery>[3]

export async function fetchQuery<T extends OperationType>(gql: GraphQLTaggedNode, variables: T['variables'], config: RelayCacheConfig = {}) {
  const environment = getClientEnvironment()!
  const result = await fetchRelayQuery<T>(environment, gql, variables, config).toPromise()

  if (hasGraphQLErrors(result)) {
    // Some errors have been included in this response. Report them to Sentry
    // The type cast is necessary because Relay does not know our internal error format
    const error = generateErrorFromGraphQLErrors(result.errors as unknown as GraphQLError[])
    trackError(
      error,
      getErrorSeverity(result.errors?.[0]) || 'high',
      graphQLErrorExtraData({ query: gql.toString(), variables }),
    )
  }

  return result
}

export type CommitMutationConfig<T extends MutationParameters> = Omit<MutationConfig<T>, 'onCompleted' | 'onError'> & {
  relayEnvironment?: RelayModernEnvironment
}

export async function commitMutation<T extends MutationParameters>(config: CommitMutationConfig<T>): Promise<T['response']> {
  const environment = config?.relayEnvironment || getClientEnvironment()!
  return new Promise((resolve, reject) => {
    commitRelayMutation<T>(environment, {
      ...omit(config, 'relayEnvironment'),
      onCompleted: (response: T['response'], errors: Nullable<readonly PayloadError[]>) => {
        if (hasGraphQLErrors(response)) {
          // The type cast is necessary because Relay does not know our internal error format
          const error = generateErrorFromGraphQLErrors(response?.errors as unknown as GraphQLError[])
          if (error) {
            console.error(error)
            trackError(error, getErrorSeverity(response?.errors?.[0]) || 'high')
          }
        }

        if (errors?.length) {
          const error = generateErrorFromGraphQLError(errors[0])
          const severity = isGraphQLError(errors[0]) ? errors[0]?.extensions?.severity : null
          trackError(error, severity || 'high')
          reject(error)
        } else {
          resolve(response as T['response'])
        }
      },
      onError: (error) => {
        trackError(error, 'high')
        reject(error)
      },
    })
  })
}

export async function requestSubscription<T extends OperationType>(options: GraphQLSubscriptionConfig<T>) {
  const environment = getClientEnvironment()!
  return requestRelaySubscription(environment, options)
}

export function invalidateRelayStore() {
  const environment = getClientEnvironment()!
  // TODO: figure out why invalidateStore is missing from the typescript types
  // Looks like the cause is outdated @types package.
  // Docs: https://relay.dev/docs/api-reference/store/
  // TODO: figure out why invalidateStore seems to not actually trigger a refresh of all the queries
  commitLocalUpdate(environment, store => (store as any).invalidateStore())
}

/**
 * Disconnect and reconnect all ActionCable channels used by Relay for GraphQL Subscriptions.
 * This can be needed if the current user has changed, to avoid receiving subscriptions for the old user.
 */
export function reconnectSubscriptions() {
  try {
    const consumer = (window as any).actionCableConsumer
    consumer.disconnect()
    consumer.connection.open()
  } catch (error) {
    trackError(error, 'high')

    return false
  }

  return true
}

export function cleanUpRelayEnvironment() {
  invalidateRelayStore()
  reconnectSubscriptions()
  clearSavedRecords()
}

export function init() {
  if (!isPrerendering()) {
    (window as any).fetchGraphQL = fetchGraphQL
  }
}

export * from './relay/updaters'
